import { type FirebaseApp, getApp, initializeApp } from 'firebase/app';
import {
    type Auth,
    type AuthProvider,
    connectAuthEmulator,
    getAuth as firebaseGetAuth,
    GoogleAuthProvider,
    signInWithPopup,
} from 'firebase/auth';
import {
    connectFirestoreEmulator,
    getFirestore as firebaseGetFirestore,
    type Firestore,
} from 'firebase/firestore';
import {
    connectStorageEmulator,
    getStorage as firebaseGetStorage,
    type FirebaseStorage,
    ref as storageRef,
    type StorageReference,
} from 'firebase/storage';
import { encodeURIPathname, splitHostPort } from 'utils/String';

import {
    getAdHubClientConfig,
    getEmulatorConfig,
    getFirebaseSdkConfig,
} from '../config';

export class StorageWrapper {
    constructor(protected readonly storage: FirebaseStorage) {}

    public ref(path?: string): StorageReference {
        return storageRef(this.storage, path);
    }

    public get maxOperationRetryTime(): number {
        return this.storage.maxOperationRetryTime;
    }
    public set maxOperationRetryTime(time: number) {
        this.storage.maxOperationRetryTime = time;
    }

    public get maxUploadRetryTime(): number {
        return this.storage.maxUploadRetryTime;
    }
    public set maxUploadRetryTime(time: number) {
        this.storage.maxUploadRetryTime = time;
    }
}

export interface WebsiteStorageOptions {
    websiteProtocol?: string;
    websiteHostname?: string;
}

/**
 * A StorageWrapper with convenience methods useful for buckets that
 * are configured for website hosting.
 */
export class WebsiteStorageWrapper extends StorageWrapper {
    public readonly websiteProtocol: string;
    public readonly websiteHostname: string;
    public readonly websiteOrigin: string;

    constructor(storage: FirebaseStorage, options?: WebsiteStorageOptions) {
        super(storage);
        let {
            websiteProtocol = 'https:',
            // destructuring means we have to pick the most permissive binding
            // eslint-disable-next-line prefer-const
            websiteHostname = storageRef(storage).bucket,
        } = options || {};

        if (!websiteProtocol.endsWith(':')) {
            websiteProtocol += ':';
        }
        if (websiteProtocol === ':') {
            throw new Error('Empty websiteProtocol');
        }
        if (!websiteHostname) {
            throw new Error('Empty websiteHostname');
        }

        this.websiteProtocol = websiteProtocol;
        this.websiteHostname = websiteHostname;
        this.websiteOrigin = `${websiteProtocol}//${websiteHostname}`;
    }

    public toWebsiteURL(pathOrReference: string | StorageReference): string {
        const ref =
            typeof pathOrReference === 'string'
                ? this.ref(pathOrReference)
                : pathOrReference;
        const url = new URL(
            encodeURIPathname(ref.fullPath),
            this.websiteOrigin
        );
        return url.href;
    }
}

export class FirebaseWrapper {
    constructor(
        public readonly auth: Auth,
        public readonly authProvider: AuthProvider,
        public readonly firestore: Firestore,
        public readonly appBucket: StorageWrapper,
        public readonly hostingBucket: WebsiteStorageWrapper
    ) {}

    public authSignInWithPopup() {
        return signInWithPopup(this.auth, this.authProvider);
    }
}

function applyEmulatorConfig(
    emulators: Record<string, string | undefined>,
    auth: Auth,
    firestore: Firestore,
    storages: ReadonlyArray<FirebaseStorage>
) {
    const logPrefix = '[Firebase emulators]';
    const report: Record<string, string> = {};

    if (emulators.firestore) {
        report.firestore = emulators.firestore;
        const [host, port] = splitHostPort(emulators.firestore);
        connectFirestoreEmulator(firestore, host, port);
    }

    if (emulators.auth) {
        report.auth = emulators.auth;
        connectAuthEmulator(auth, `http://${emulators.auth}`);
    }

    if (emulators.storage) {
        const [host, port] = splitHostPort(emulators.storage);
        for (const storageInst of storages) {
            const bucketName = storageRef(storageInst).bucket;
            report[`storage:${bucketName}`] = emulators.storage;
            connectStorageEmulator(storageInst, host, port);
        }
    }

    if (Object.keys(report).length === 0) {
        console.log(`${logPrefix} No emulators configured`);
    } else {
        console.log(logPrefix);
        console.table(report);
    }
}

function createFirebase() {
    const sdkConfig = getFirebaseSdkConfig();
    const appConfig = getAdHubClientConfig();

    let app: FirebaseApp;
    let firebaseNeedsInit = false;
    try {
        app = getApp();
    } catch (_getAppError) {
        app = initializeApp(sdkConfig);
        firebaseNeedsInit = true;
    }

    const auth = firebaseGetAuth(app);
    const authProvider = new GoogleAuthProvider();
    const firestore = firebaseGetFirestore(app);

    // sdkConfig and appConfig refer to storage buckets by name only.
    // Firebase 9+'s getStorage requires a gs:// URL
    const appStorage = firebaseGetStorage(
        app,
        `gs://${sdkConfig.storageBucket}`
    );
    const hostingStorage = firebaseGetStorage(
        app,
        `gs://${appConfig.hostingBucket}`
    );

    if (firebaseNeedsInit) {
        applyEmulatorConfig(getEmulatorConfig(), auth, firestore, [
            appStorage,
            hostingStorage,
        ]);
    }

    const appBucket = new StorageWrapper(appStorage);
    const hostingBucket = new WebsiteStorageWrapper(hostingStorage);

    return new FirebaseWrapper(
        auth,
        authProvider,
        firestore,
        appBucket,
        hostingBucket
    );
}

let instance: FirebaseWrapper | null = null;
export function getFirebase(): FirebaseWrapper {
    if (!instance) {
        instance = createFirebase();
    }
    return instance;
}

export const getAppBucket = () => getFirebase().appBucket;
export const getAuth = () => getFirebase().auth;
export const getAuthProvider = () => getFirebase().authProvider;
export const getFirestore = () => getFirebase().firestore;
export const getHostingBucket = () => getFirebase().hostingBucket;

export function getAppStorageFileRef(storagePath: string) {
    return getAppBucket().ref(storagePath);
}

export function getHostingStorageFileRef(storagePath: string) {
    return getHostingBucket().ref(storagePath);
}

export function hostingStoragePathToUrl(storagePath: string) {
    return getHostingBucket().toWebsiteURL(storagePath);
}
